// @ts-nocheck
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Grid,
  Card,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import StarIcon from '@mui/icons-material/Star';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';
import ClientOrderService, { ClientOrder } from '../../service/clientOrder.service';

export default function OrderTrackerPage() {
  const [orders, setOrders] = useState<ClientOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [tagFilter, setTagFilter] = useState<string>('all');

  // Modals state
  const [selectedOrderForTrack, setSelectedOrderForTrack] = useState<ClientOrder | null>(null);
  const [selectedOrderForDetail, setSelectedOrderForDetail] = useState<ClientOrder | null>(null);
  const [selectedAlertForDetail, setSelectedAlertForDetail] = useState<ClientOrder | null>(null);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const data = await ClientOrderService.getOrders();
      setOrders(data);
    } catch (err) {
      toast.error('Could not fetch latest orders. Using local functional dataset.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // Filtered orders list
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const matchSearch =
        order.orderId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.orderDetail.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.vendorDetail.vendorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.orderStatus.trackingNumber.toLowerCase().includes(searchQuery.toLowerCase());

      const matchStatus =
        statusFilter === 'all' ||
        order.orderStatus.currentStageLabel.toLowerCase() === statusFilter.toLowerCase();

      const matchTag =
        tagFilter === 'all' ||
        order.orderTag.alertType.toLowerCase().includes(tagFilter.toLowerCase()) ||
        order.orderTag.category.toLowerCase().includes(tagFilter.toLowerCase());

      return matchSearch && matchStatus && matchTag;
    });
  }, [orders, searchQuery, statusFilter, tagFilter]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`Copied "${text}" to clipboard!`);
  };

  const getTagColor = (alertType: string) => {
    switch (alertType) {
      case 'Fall Short':
        return {
          bg: '#fef2f2',
          text: '#991b1b',
          border: '#fecaca',
          icon: <WarningAmberIcon sx={{ fontSize: 16, color: '#ef4444' }} />,
        };
      case 'Availability':
        return {
          bg: '#fffbeb',
          text: '#92400e',
          border: '#fde68a',
          icon: <WarningAmberIcon sx={{ fontSize: 16, color: '#f59e0b' }} />,
        };
      case 'Excessive Compliance':
        return {
          bg: '#f5f3ff',
          text: '#5b21b6',
          border: '#ddd6fe',
          icon: <InfoOutlinedIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />,
        };
      default:
        return {
          bg: '#ecfdf5',
          text: '#065f46',
          border: '#a7f3d0',
          icon: <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#10b981' }} />,
        };
    }
  };

  const getStatusColor = (statusLabel: string) => {
    switch (statusLabel) {
      case 'Delivered':
        return { bg: '#dcfce7', text: '#15803d', border: '#bbf7d0' };
      case 'In Transit':
        return { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' };
      case 'Dispatched':
        return { bg: '#fef3c7', text: '#b45309', border: '#fde68a' };
      case 'In Production':
        return { bg: '#fdf4ff', text: '#86198f', border: '#f5d0fe' };
      default:
        return { bg: '#f3f4f6', text: '#374151', border: '#e5e7eb' };
    }
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Page Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#3B3027' }}>
              Order Tracker
            </Typography>
            <Chip
              label="Live Consignment Data"
              size="small"
              sx={{ bgcolor: '#A77B58', color: '#FFFFFF', fontWeight: 700, fontSize: '11px' }}
            />
          </Box>
          <Typography sx={{ fontSize: '13px', color: '#756B63', mt: 0.5 }}>
            Real-time client orders, supplier connect alert tags, and active shipment tracking
          </Typography>
        </Box>

        {/* Quick KPI stats */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Chip
            label={`Total: ${orders.length} Orders`}
            variant="outlined"
            sx={{ fontWeight: 700, borderColor: '#D6CEC4', color: '#3B3027', bgcolor: '#fff' }}
          />
          <Chip
            label={`In Transit: ${orders.filter((o) => o.orderStatus.currentStageLabel === 'In Transit').length}`}
            sx={{ fontWeight: 700, bgcolor: '#dbeafe', color: '#1e40af' }}
          />
          <Chip
            icon={<WarningAmberIcon style={{ fontSize: 16, color: '#b45309' }} />}
            label={`Alerts Tagged: ${orders.filter((o) => o.orderTag.alertType !== 'Normal').length}`}
            sx={{ fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }}
          />
        </Box>
      </Box>

      {/* Filter and Search Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2.5,
          border: '1px solid #E8DFD5',
          bgcolor: '#FFFFFF',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <TextField
          placeholder="Search by Order ID, Product name, Vendor, or Tracking #..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: { xs: '100%', sm: 280 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#A77B58' }} />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap' }}>
          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Order Status</InputLabel>
            <Select
              value={statusFilter}
              label="Order Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="Confirmed">Confirmed</MenuItem>
              <MenuItem value="In Production">In Production</MenuItem>
              <MenuItem value="Dispatched">Dispatched</MenuItem>
              <MenuItem value="In Transit">In Transit</MenuItem>
              <MenuItem value="Delivered">Delivered</MenuItem>
            </Select>
          </FormControl>

          {/* Supplier Connect Alert Tag Filter */}
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Supplier Alert Tag</InputLabel>
            <Select
              value={tagFilter}
              label="Supplier Alert Tag"
              onChange={(e) => setTagFilter(e.target.value)}
            >
              <MenuItem value="all">All Alert Tags</MenuItem>
              <MenuItem value="Fall Short">Fall Short</MenuItem>
              <MenuItem value="Availability">Availability</MenuItem>
              <MenuItem value="Excessive Compliance">Compliance</MenuItem>
              <MenuItem value="Normal">Normal / On Schedule</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            size="small"
            onClick={fetchOrders}
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            sx={{
              borderColor: '#D6CEC4',
              color: '#5F4B3B',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { borderColor: '#A77B58', bgcolor: 'rgba(167, 123, 88, 0.05)' },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {/* Main Order Tracker Table - Matches the exact 5 columns in the user sketch */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid #E8DFD5',
          bgcolor: '#FFFFFF',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(95, 75, 59, 0.05)',
        }}
      >
        <Table sx={{ minWidth: 950 }}>
          <TableHead sx={{ bgcolor: '#FAF6F0' }}>
            <TableRow>
              {/* 1. O.Id */}
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13.5px', py: 2, width: '13%' }}>
                O.Id
              </TableCell>

              {/* 2. O. Detail */}
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13.5px', py: 2, width: '25%' }}>
                O. Detail
              </TableCell>

              {/* 3. O. Tag (Note from sketch: "Tag name as given in supplier connect based on Alert.") */}
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13.5px', py: 2, width: '20%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>O. Tag</span>
                  <Tooltip title="Tag name as given in supplier connect based on Alert (Fall Short, Availability, Compliance)">
                    <InfoOutlinedIcon sx={{ fontSize: 15, color: '#A77B58', cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
                <Typography sx={{ fontSize: '10px', color: '#A77B58', fontWeight: 600 }}>
                  Supplier Connect Alert
                </Typography>
              </TableCell>

              {/* 4. O. Status (Mini progress / tracker with Track button as drawn in sketch) */}
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13.5px', py: 2, width: '22%' }}>
                <span>O. Status</span>
                <Typography sx={{ fontSize: '10px', color: '#756B63', fontWeight: 500 }}>
                  Pipeline & Live Track
                </Typography>
              </TableCell>

              {/* 5. Vendor Detail */}
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13.5px', py: 2, width: '20%' }}>
                Vendor Detail
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <CircularProgress sx={{ color: '#A77B58' }} />
                  <Typography sx={{ mt: 2, color: '#756B63', fontSize: '13px' }}>
                    Loading functional client orders...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <Typography sx={{ fontWeight: 700, color: '#5F4B3B', fontSize: '15px' }}>
                    No orders matching criteria
                  </Typography>
                  <Typography sx={{ color: '#756B63', fontSize: '13px', mt: 0.5 }}>
                    Try clearing filters or search query to see active orders.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredOrders.map((order) => {
                const tagStyle = getTagColor(order.orderTag.alertType);
                const statusStyle = getStatusColor(order.orderStatus.currentStageLabel);
                const stages = ['Confirmed', 'In Production', 'Dispatched', 'In Transit', 'Delivered'];

                return (
                  <TableRow
                    key={order.id}
                    hover
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    {/* 1. O.Id */}
                    <TableCell sx={{ verticalAlign: 'top', py: 2.2 }}>
                      <Box
                        onClick={() => setSelectedOrderForDetail(order)}
                        sx={{
                          cursor: 'pointer',
                          display: 'inline-block',
                          '&:hover .order-id': { color: '#A77B58', textDecoration: 'underline' },
                        }}
                      >
                        <Typography
                          className="order-id"
                          sx={{ fontWeight: 800, fontSize: '14px', color: '#3B3027' }}
                        >
                          {order.orderId}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '11px', color: '#756B63', mt: 0.5 }}>
                        Date: {order.orderDate}
                      </Typography>
                      <Chip
                        label={order.invoiceNo || 'Proforma INV'}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '10px',
                          mt: 0.8,
                          bgcolor: '#FAF6F0',
                          color: '#5F4B3B',
                          fontWeight: 600,
                          border: '1px solid #E8DFD5',
                        }}
                      />
                    </TableCell>

                    {/* 2. O. Detail */}
                    <TableCell sx={{ verticalAlign: 'top', py: 2.2 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '13.5px', color: '#3B3027', lineHeight: 1.3 }}>
                        {order.orderDetail.productName}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '11.5px',
                          color: '#756B63',
                          mt: 0.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {order.orderDetail.specifications}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#A77B58' }}>
                          Qty: {order.orderDetail.quantity} {order.orderDetail.unit}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', fontWeight: 800, color: '#059669' }}>
                          {order.orderDetail.totalAmount}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        onClick={() => setSelectedOrderForDetail(order)}
                        startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                        sx={{
                          p: 0,
                          mt: 0.8,
                          minWidth: 'auto',
                          textTransform: 'none',
                          fontSize: '11px',
                          color: '#756B63',
                          fontWeight: 600,
                          '&:hover': { color: '#A77B58', bgcolor: 'transparent' },
                        }}
                      >
                        View Full Specs & Port Details
                      </Button>
                    </TableCell>

                    {/* 3. O. Tag (Supplier Connect Alert Based) */}
                    <TableCell sx={{ verticalAlign: 'top', py: 2.2 }}>
                      <Box
                        onClick={() => setSelectedAlertForDetail(order)}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.8,
                          px: 1.4,
                          py: 0.8,
                          borderRadius: 2,
                          bgcolor: tagStyle.bg,
                          border: `1px solid ${tagStyle.border}`,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        {tagStyle.icon}
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: tagStyle.text }}>
                          {order.orderTag.tagName}
                        </Typography>
                      </Box>

                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: '#756B63',
                          mt: 0.8,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {order.orderTag.reasonDesc}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.8 }}>
                        <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: '#9E948C' }}>
                          Status:
                        </Typography>
                        <Chip
                          label={order.orderTag.actionStatus}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '9.5px',
                            fontWeight: 700,
                            bgcolor:
                              order.orderTag.actionStatus === 'Resolved'
                                ? '#ecfdf5'
                                : order.orderTag.actionStatus === 'In Progress'
                                ? '#fef3c7'
                                : '#f3f4f6',
                            color:
                              order.orderTag.actionStatus === 'Resolved'
                                ? '#059669'
                                : order.orderTag.actionStatus === 'In Progress'
                                ? '#d97706'
                                : '#6b7280',
                          }}
                        />
                      </Box>
                    </TableCell>

                    {/* 4. O. Status (Mini progress tracker + Track button with truck icon) */}
                    <TableCell sx={{ verticalAlign: 'top', py: 2.2 }}>
                      {/* Mini Progress Bar / Steps as drawn in the sketch */}
                      <Box sx={{ mb: 1.2, p: 1, bgcolor: '#FAF8F5', borderRadius: 2, border: '1px solid #EFEAE3' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                          <Chip
                            label={order.orderStatus.currentStageLabel}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '10.5px',
                              fontWeight: 800,
                              bgcolor: statusStyle.bg,
                              color: statusStyle.text,
                              border: `1px solid ${statusStyle.border}`,
                            }}
                          />
                          <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: '#756B63' }}>
                            {order.orderStatus.progressPercent}%
                          </Typography>
                        </Box>

                        {/* Step Dots Pipeline */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5 }}>
                          {stages.map((stageName, sIdx) => {
                            const isPassed = sIdx <= order.orderStatus.currentStageIndex;
                            const isCurrent = sIdx === order.orderStatus.currentStageIndex;
                            return (
                              <Tooltip key={stageName} title={`Stage ${sIdx + 1}: ${stageName}`}>
                                <Box
                                  sx={{
                                    flex: 1,
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: isPassed
                                      ? isCurrent
                                        ? '#A77B58'
                                        : '#10b981'
                                      : '#E8DFD5',
                                  }}
                                />
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </Box>

                      {/* Explicit [Track] Button with Truck icon as hand-drawn in sketch */}
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setSelectedOrderForTrack(order)}
                        startIcon={<LocalShippingIcon sx={{ fontSize: 18 }} />}
                        sx={{
                          width: '100%',
                          py: 0.8,
                          bgcolor: '#5F4B3B',
                          color: '#FFFFFF',
                          fontWeight: 700,
                          fontSize: '12px',
                          textTransform: 'none',
                          borderRadius: 2,
                          boxShadow: '0 2px 6px rgba(95, 75, 59, 0.25)',
                          '&:hover': {
                            bgcolor: '#3E3126',
                          },
                        }}
                      >
                        Track Shipment
                      </Button>

                      <Typography sx={{ fontSize: '10.5px', color: '#9E948C', mt: 0.8, textAlign: 'center' }}>
                        ETA: {order.orderStatus.estimatedDelivery}
                      </Typography>
                    </TableCell>

                    {/* 5. Vendor Detail */}
                    <TableCell sx={{ verticalAlign: 'top', py: 2.2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '13.5px', color: '#3B3027' }}>
                          {order.vendorDetail.vendorName}
                        </Typography>
                        {order.vendorDetail.verified && (
                          <Tooltip title="Verified Supplier">
                            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#10b981' }} />
                          </Tooltip>
                        )}
                      </Box>

                      <Typography sx={{ fontSize: '11.5px', color: '#756B63', mt: 0.3 }}>
                        Contact: {order.vendorDetail.contactPerson}
                      </Typography>

                      <Typography sx={{ fontSize: '11px', color: '#756B63', mt: 0.3 }}>
                        📍 {order.vendorDetail.city}, {order.vendorDetail.country}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mt: 0.8 }}>
                        <Tooltip title={order.vendorDetail.email}>
                          <IconButton
                            size="small"
                            href={`mailto:${order.vendorDetail.email}`}
                            sx={{ p: 0.5, bgcolor: '#FAF6F0', color: '#A77B58' }}
                          >
                            <EmailIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={order.vendorDetail.phone}>
                          <IconButton
                            size="small"
                            href={`tel:${order.vendorDetail.phone}`}
                            sx={{ p: 0.5, bgcolor: '#FAF6F0', color: '#A77B58' }}
                          >
                            <PhoneIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, ml: 'auto' }}>
                          <StarIcon sx={{ fontSize: 14, color: '#f59e0b' }} />
                          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#3B3027' }}>
                            {order.vendorDetail.rating}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ========================================================================= */}
      {/* 1. LIVE TRACKING MODAL (Triggered by the [Track] button with Truck Icon) */}
      {/* ========================================================================= */}
      <Dialog
        open={Boolean(selectedOrderForTrack)}
        onClose={() => setSelectedOrderForTrack(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            border: '1px solid #E8DFD5',
          },
        }}
      >
        {selectedOrderForTrack && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <LocalShippingIcon sx={{ color: '#A77B58', fontSize: 26 }} />
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#3B3027' }}>
                    Shipment Tracking • {selectedOrderForTrack.orderId}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.3 }}>
                  Carrier: {selectedOrderForTrack.orderStatus.carrier}
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedOrderForTrack(null)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ borderColor: '#E8DFD5' }}>
              {/* Tracking ID Header Card */}
              <Card
                elevation={0}
                sx={{
                  bgcolor: '#FAF6F0',
                  border: '1px solid #E8DFD5',
                  borderRadius: 2,
                  p: 2,
                  mb: 3,
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: '11px', color: '#756B63', fontWeight: 600 }}>
                      BILL OF LADING / TRACKING NUMBER
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#3B3027', letterSpacing: 0.5 }}>
                        {selectedOrderForTrack.orderStatus.trackingNumber}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(selectedOrderForTrack.orderStatus.trackingNumber)}
                      >
                        <ContentCopyIcon sx={{ fontSize: 16, color: '#A77B58' }} />
                      </IconButton>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '11px', color: '#756B63', fontWeight: 600 }}>
                      ESTIMATED ARRIVAL
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#059669', mt: 0.5 }}>
                      {selectedOrderForTrack.orderStatus.estimatedDelivery}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '11px', color: '#756B63', fontWeight: 600 }}>
                      CURRENT LOCATION
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1e40af', mt: 0.5 }}>
                      {selectedOrderForTrack.orderStatus.currentLocation}
                    </Typography>
                  </Grid>
                </Grid>
              </Card>

              {/* Alert Tag Warning inside Tracking Modal if applicable */}
              {selectedOrderForTrack.orderTag.alertType !== 'Normal' && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#fffbeb',
                    border: '1px solid #fde68a',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                  }}
                >
                  <WarningAmberIcon sx={{ color: '#d97706', mt: 0.3 }} />
                  <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#92400e' }}>
                      Supplier Connect Active Alert: {selectedOrderForTrack.orderTag.tagName}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#78350f', mt: 0.3 }}>
                      {selectedOrderForTrack.orderTag.reasonDesc}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Transit Ports Route summary */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  bgcolor: '#FFFFFF',
                  border: '1px solid #E8DFD5',
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: '10.5px', color: '#756B63', fontWeight: 600 }}>
                    ORIGIN SEA PORT
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#3B3027' }}>
                    {selectedOrderForTrack.orderDetail.originPort}
                  </Typography>
                </Box>
                <LocalShippingIcon sx={{ color: '#A77B58', fontSize: 24 }} />
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '10.5px', color: '#756B63', fontWeight: 600 }}>
                    DISCHARGE DESTINATION
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#3B3027' }}>
                    {selectedOrderForTrack.orderDetail.destinationPort}
                  </Typography>
                </Box>
              </Box>

              {/* Detailed Vertical Stepper for Milestones */}
              <Typography sx={{ fontWeight: 800, fontSize: '14px', color: '#3B3027', mb: 2 }}>
                Shipment Milestones & Transit History
              </Typography>

              <Stepper orientation="vertical">
                {selectedOrderForTrack.orderStatus.milestones.map((milestone, idx) => {
                  const isDone = milestone.status === 'completed';
                  const isActive = milestone.status === 'active';
                  return (
                    <Step key={idx} active={isActive} completed={isDone}>
                      <StepLabel
                        StepIconProps={{
                          sx: {
                            color: isDone ? '#10b981' : isActive ? '#A77B58' : '#D6CEC4',
                            '&.Mui-active': { color: '#A77B58' },
                            '&.Mui-completed': { color: '#10b981' },
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '13.5px', color: '#3B3027' }}>
                            {milestone.stage}
                          </Typography>
                          <Typography sx={{ fontSize: '11px', color: '#756B63' }}>
                            {milestone.date}
                          </Typography>
                        </Box>
                      </StepLabel>
                      <StepContent>
                        <Typography sx={{ fontSize: '12.5px', color: '#5F4B3B', pb: 1.5 }}>
                          {milestone.note}
                        </Typography>
                      </StepContent>
                    </Step>
                  );
                })}
              </Stepper>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setSelectedOrderForTrack(null)}
                sx={{ color: '#756B63', textTransform: 'none', fontWeight: 600 }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  toast.info(`Checking live AIS satellite tracking for ${selectedOrderForTrack.orderStatus.carrier}...`);
                }}
                sx={{
                  bgcolor: '#A77B58',
                  '&:hover': { bgcolor: '#936948' },
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                Refresh Satellite Position
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ========================================================================= */}
      {/* 2. ORDER DETAILS MODAL (Product Specs, Commercial Invoices, Shipping)    */}
      {/* ========================================================================= */}
      <Dialog
        open={Boolean(selectedOrderForDetail)}
        onClose={() => setSelectedOrderForDetail(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        {selectedOrderForDetail && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#3B3027' }}>
                  Order Details • {selectedOrderForDetail.orderId}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63' }}>
                  Invoice Ref: {selectedOrderForDetail.invoiceNo} | Date: {selectedOrderForDetail.orderDate}
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedOrderForDetail(null)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ borderColor: '#E8DFD5' }}>
              <Grid container spacing={3}>
                {/* Product & Specifications */}
                <Grid item xs={12} md={7}>
                  <Card elevation={0} sx={{ border: '1px solid #E8DFD5', p: 2, height: '100%' }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 800, color: '#A77B58', textTransform: 'uppercase' }}>
                      Product & Commercial Specification
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#3B3027', mt: 1 }}>
                      {selectedOrderForDetail.orderDetail.productName}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                      Category: {selectedOrderForDetail.orderDetail.productCategory}
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography sx={{ fontSize: '13px', color: '#5F4B3B', lineHeight: 1.6 }}>
                      <strong>Technical Parameters:</strong> {selectedOrderForDetail.orderDetail.specifications}
                    </Typography>
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: '#FAF6F0', borderRadius: 2 }}>
                      <Typography sx={{ fontSize: '12px', color: '#756B63' }}>
                        <strong>Incoterms:</strong> {selectedOrderForDetail.orderDetail.incoterm}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                        <strong>Payment Terms:</strong> {selectedOrderForDetail.orderDetail.paymentTerms}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                        <strong>Origin Port:</strong> {selectedOrderForDetail.orderDetail.originPort}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                        <strong>Destination Port:</strong> {selectedOrderForDetail.orderDetail.destinationPort}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>

                {/* Financial Breakdown & Vendor */}
                <Grid item xs={12} md={5}>
                  <Card elevation={0} sx={{ border: '1px solid #E8DFD5', p: 2, bgcolor: '#FAF8F5' }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 800, color: '#A77B58', textTransform: 'uppercase' }}>
                      Financial Summary
                    </Typography>
                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '13px', color: '#756B63' }}>Quantity</Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#3B3027' }}>
                        {selectedOrderForDetail.orderDetail.quantity} {selectedOrderForDetail.orderDetail.unit}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '13px', color: '#756B63' }}>Unit Rate</Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#3B3027' }}>
                        {selectedOrderForDetail.orderDetail.unitPrice}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#3B3027' }}>Total Amount</Typography>
                      <Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#059669' }}>
                        {selectedOrderForDetail.orderDetail.totalAmount}
                      </Typography>
                    </Box>
                  </Card>

                  <Card elevation={0} sx={{ border: '1px solid #E8DFD5', p: 2, mt: 2 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 800, color: '#A77B58', textTransform: 'uppercase' }}>
                      Contracted Supplier
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#3B3027', mt: 1 }}>
                      {selectedOrderForDetail.vendorDetail.vendorName}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.3 }}>
                      {selectedOrderForDetail.vendorDetail.contactPerson}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.3 }}>
                      📧 {selectedOrderForDetail.vendorDetail.email}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.3 }}>
                      📞 {selectedOrderForDetail.vendorDetail.phone}
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setSelectedOrderForDetail(null)}
                sx={{ color: '#756B63', textTransform: 'none', fontWeight: 600 }}
              >
                Close
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => toast.success(`Downloading Proforma Invoice ${selectedOrderForDetail.invoiceNo}...`)}
                sx={{
                  borderColor: '#D6CEC4',
                  color: '#5F4B3B',
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                Download Proforma (PDF)
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ========================================================================= */}
      {/* 3. SUPPLIER CONNECT ALERT MODAL (Triggered by clicking O. Tag)            */}
      {/* ========================================================================= */}
      <Dialog
        open={Boolean(selectedAlertForDetail)}
        onClose={() => setSelectedAlertForDetail(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        {selectedAlertForDetail && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningAmberIcon sx={{ color: '#d97706', fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#3B3027' }}>
                  Supplier Connect Alert Details
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedAlertForDetail(null)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ borderColor: '#E8DFD5' }}>
              <Box sx={{ p: 2, bgcolor: '#FAF6F0', borderRadius: 2, mb: 2 }}>
                <Typography sx={{ fontSize: '11px', color: '#756B63', fontWeight: 700 }}>
                  ALERT TAG IDENTIFIER
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#92400e', mt: 0.5 }}>
                  {selectedAlertForDetail.orderTag.tagName}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip
                    label={`Type: ${selectedAlertForDetail.orderTag.alertType}`}
                    size="small"
                    sx={{ bgcolor: '#fff', fontWeight: 600, fontSize: '11px' }}
                  />
                  <Chip
                    label={`Category: ${selectedAlertForDetail.orderTag.category}`}
                    size="small"
                    sx={{ bgcolor: '#fff', fontWeight: 600, fontSize: '11px' }}
                  />
                  <Chip
                    label={`Status: ${selectedAlertForDetail.orderTag.actionStatus}`}
                    size="small"
                    sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: '11px' }}
                  />
                </Box>
              </Box>

              <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#3B3027', mb: 0.5 }}>
                Supplier Reported Reason Description:
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#5F4B3B', lineHeight: 1.6, mb: 2 }}>
                {selectedAlertForDetail.orderTag.reasonDesc}
              </Typography>

              <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#3B3027', mb: 0.5 }}>
                Associated Consignment:
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#756B63' }}>
                Order #{selectedAlertForDetail.orderId} • {selectedAlertForDetail.orderDetail.productName}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                Supplier: {selectedAlertForDetail.vendorDetail.vendorName}
              </Typography>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setSelectedAlertForDetail(null)}
                sx={{ color: '#756B63', textTransform: 'none', fontWeight: 600 }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
